import React, { ReactNode, useMemo } from 'react';

import { Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { PublicRoutes } from './PublicRoutes';
import { RootRedirect } from './RootRedirect';
import AppSuspense from 'components/AppSuspense';
import { ErrorBoundary } from 'components/ErrorBoundary';
import LayoutSwitcher from 'components/Layout/LayoutSwitcher';
import RequireMenuInitialized from 'components/RequireMenuInitialized';
import { Spin } from 'components/Spin';
import { UnderConstruction } from 'components/UnderConstruction';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import RouteGuard from 'routes/RouteGuard';
import { lazyLoad, lazyLoadNamed } from 'utils/lazyLoad';

// ===================================================================
// PHASE 6: CODE SPLITTING - Lazy Loading Implementation 🚀
// ===================================================================
// All pages are now loaded on-demand to reduce initial bundle size
// Using lazyLoad utility with automatic retry logic for reliability
// Expected: Main bundle reduction from ~1.15 MB to ~400-600 KB
// ===================================================================

// Layout Components (loaded eagerly - critical for initial render)

// Error Page - Lazy loaded with retry for 404 rendering
const Error = lazyLoad(() => import('pages/Errors/Error'));

// Lazy Loaded Pages - Core Features
const Cockpit = lazyLoadNamed(() => import('pages/Cockpit/Cockpit'), 'Cockpit');
const BasicPersonalData = lazyLoad(() => import('pages/BasicPersonalData'));
const IndividualBio = lazyLoad(() => import('pages/IndividualBio'));
const CompanyData = lazyLoad(() => import('pages/CompanyData'));
const BusinessBio = lazyLoad(() => import('pages/BusinessBio'));
const CollectiveProfileMembers = lazyLoad(() => import('pages/CollectiveProfileMembers'));

// Lazy Loaded Pages - Education
const EducationShowcase = lazyLoadNamed(
  () => import('pages/EducationShowcase'),
  'EducationShowcase'
);
const EducationMyCourses = lazyLoadNamed(
  () => import('pages/EducationMyCourses'),
  'EducationMyCourses'
);
const EducationFavorites = lazyLoadNamed(
  () => import('pages/EducationFavorites'),
  'EducationFavorites'
);
const OrchestratorEducationCategories = lazyLoadNamed(
  () => import('pages/OrchestratorEducationCategories'),
  'OrchestratorEducationCategories'
);
const OrchestratorEducationCourses = lazyLoad(() => import('pages/OrchestratorEducationCourses'));
const OrchestratorEducationLessons = lazyLoad(() => import('pages/OrchestratorEducationLessons'));

// Lazy Loaded Pages - Networking & Events
const NetworkingExperience = lazyLoadNamed(
  () => import('pages/NetworkingExperience'),
  'NetworkingExperience'
);
const InvitesPage = lazyLoad(() => import('pages/Invites'));

// Lazy Loaded Pages - Digital Governance
const GovernanceThematicGroups = lazyLoad(
  () => import('pages/DigitalGovernance/GovernanceThematicGroups')
);
const GovernanceUsers = lazyLoad(() => import('pages/DigitalGovernance/GovernanceUsers'));
const GovernanceEvents = lazyLoad(() => import('pages/DigitalGovernance/GovernanceEvent'));

// Lazy Loaded Pages - Agenda & Automation
const AgendaConfiguration = lazyLoad(() => import('pages/AgendaConfiguration'));
const AutomationV2 = lazyLoad(() => import('pages/AutomationV2'));

// Lazy Loaded Pages - Lead Request & Forms
const LeadRequestOrchestration = lazyLoad(
  () => import('pages/LeadRequest/LeadRequestOrchestration')
);
const SponsorRequest = lazyLoad(() => import('pages/LeadRequest/SponsorRequest'));
const EndUserResponse = lazyLoad(() => import('pages/LeadRequest/EndUserResponse'));
const FormsManagement = lazyLoad(() => import('pages/FormsManagement'));

// Lazy Loaded Pages - Marketplace
const SolutionsMarketplace = lazyLoad(() => import('pages/Marketplace'));

// Lazy Loaded Pages - Other
const PageRedirect = lazyLoad(() => import('pages/PageRedirect'));

// TEMPORARY: Visual Regression Test for AppLoading
const AppLoadingVisualTest = lazyLoadNamed(
  () => import('pages/AppLoadingVisualTest'),
  'AppLoadingVisualTest'
);

// Lazy Loaded Pages - Settings
const SettingsProfile = lazyLoadNamed(() => import('pages/SettingsProfile'), 'SettingsProfile');

// Lazy Loaded Microfrontends - Meetings & Advisors
const AccessHub = lazyLoad(() => import('microfrontends/Meetings/AccessHub'));
const AdvisorsAccessHub = lazyLoad(() => import('microfrontends/Advisors/AccessHub/Advisors'));

// Lazy Loaded Microfrontends - Marketplace
const AdvisorsMarketplace = lazyLoad(() => import('microfrontends/Marketplace/Advisors'));
const GeneralMarketplace = lazyLoad(() => import('microfrontends/Marketplace/General'));
const ServicesMarketplace = lazyLoad(() => import('microfrontends/Marketplace/Services'));

interface RouteComponent {
  documentId: string;
  component: React.ReactNode;
  path?: string;
  isPublic?: boolean;
}

/**
 * Wrapper component that provides ErrorBoundary with location-based reset.
 * This ensures error state is cleared when navigating to a new route.
 */
const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();

  return <ErrorBoundary resetKeys={[location.pathname]}>{children}</ErrorBoundary>;
};

const AuthRoutes = () => {
  // Hooks required for route mapping
  const { t } = useTranslation('marketplace');
  const menuRoutes = useMenuRoutes();

  // All useMemo hooks MUST come here
  const publicRoutes = useMemo<RouteComponent[]>(() => PublicRoutes, []);

  const protectedRoutes = useMemo<RouteComponent[]>(() => {
    // Map components by documentId
    const getComponentMap = (documentId: string) => {
      const componentMap: Record<string, React.ReactNode> = {
        'marketplace-advisor': <SolutionsMarketplace type="advisor" title={t('labels.advisors')} />,
        'marketplace-courses': (
          <SolutionsMarketplace type="courses" title={t('labels.courses')} categoryId={1} />
        ),
        'marketplace-services': <SolutionsMarketplace type="services" title={t('labels.services')} />,
        'marketplace-plans': <SolutionsMarketplace type="plans" title={t('labels.plans')} categoryId={5} />,
        bhnrs7gefue5v5ntzqg3ls74: <Cockpit isAgenda={false} />,
        eh04q5jlhm52tnh1tthycrkd: <Cockpit isAgenda={true} />,
        c7jp5768k53sra3u6bv5ynxg: <IndividualBio />,
        orggc8xfsjhi06uojgmcnifc: <CollectiveProfileMembers />,
        md1wm0c8mnn6t8j0k62aywmy: <EducationShowcase />,
        blm0udkzzi2tixjhb6knp3h4: <EducationMyCourses />,
        bf2t3hejquw6g50ok47yujs8: <EducationFavorites />,
        x8vn7552j2z8motvcpzq2dp5: <NetworkingExperience key="networking-experience-component" />,
        // xxo09iaggq9nwdz1k6384su7: <PBIDash reportName="rmkr_general_networks" />,
        // ij94qlo086g27564ecpwuodc: <PBIDash reportName="rmkr_bio_networks" />,
        // qk2dzqrdfefnut2qtisy2zbl: <PBIDash reportName="rmkr_extended_community" />,
        // ld9rsfvvtjxk1awhj65vrmit: <PBIDash reportName="rmkr_individual_network" />,
        // wkfn4hkgdw13y86o9yxu41pq: <PBIDash reportName="rmkr_general_invitee_profile" />,
        xxo09iaggq9nwdz1k6384su7: <UnderConstruction variant="fullscreen" />,
        ij94qlo086g27564ecpwuodc: <UnderConstruction variant="fullscreen" />,
        qk2dzqrdfefnut2qtisy2zbl: <UnderConstruction variant="fullscreen" />,
        ld9rsfvvtjxk1awhj65vrmit: <UnderConstruction variant="fullscreen" />,
        wkfn4hkgdw13y86o9yxu41pq: <UnderConstruction variant="fullscreen" />,
        ah06bmqiaqi7oxigam2836u1: <BasicPersonalData />,

        // PHASE 3: Company Data - MIGRATED to shadcn/ui ✅
        yuheaauamsvg289gn5ac2ogk: <CompanyData />,

        nwdjh4f3wj95xej5a30iri2q: <InvitesPage />,
        wfrhqdqgq51gqj4kcswgvg0r: <GovernanceThematicGroups />,
        b0i2qi0dhqmelqonbhn3pzn5: <GovernanceUsers />,
        bv5n1nkso2vewyx5gilyblgu: <GovernanceEvents />,
        qec5xuvx1dq05uuczz52irrq: <OrchestratorEducationCategories />,
        nhaa2ypwl4zbsfu8xkaf0k17: <OrchestratorEducationCourses key="courses" />,
        opp497btmueyywpgzbr7e835: <OrchestratorEducationLessons key="lessons" />,
        tegujpnuqitwwjxdw3mqhf00: <UnderConstruction variant="fullscreen" />,
        meeting_room: <></>,
        rp6pr14xcoibtn5p9qd0ip8z: <AdvisorsMarketplace />,
        nql7so3er7p473mkgzawkla7: <GeneralMarketplace categoryId={1} />,
        hqqnrfpu1ox2iqdzoa81pmt3: <GeneralMarketplace categoryId={5} />,
        f0pzvvpx9kvxz2ejkubibdys: <GeneralMarketplace categoryId={11} />,
        g58dvhpca9eirq6gln0f2fyi: <ServicesMarketplace />,

        // PHASE 3: Agenda Configuration - MIGRATED to shadcn/ui ✅
        s56a1gugf03ngwgsp4zr3nho: <AgendaConfiguration />,

        vpquicugxm4w5svpjji0b3ox: <AccessHub />,
        ttuofh4gmf7xs8vv8ee6xi6s: <AdvisorsAccessHub />,
        lqzbm5ir5gnxh1zsdtufcd7w: <AutomationV2 />,
        ki9ao5w75ds5ykbeytbvm2dd: <BusinessBio />,
        k2acxd0qf2eiwtevt4xn5oa1: <LeadRequestOrchestration />,
        lk0yriz57vt600loevyonvq2: <SponsorRequest />,
        jxm4lel4ot7ihnap5xwkmtyf: <EndUserResponse />,
        pivrzyizslnoydu96jbgf674: <PageRedirect documentId="pivrzyizslnoydu96jbgf674" />,
        wrr86kqrv5d0c5o47me5nqki: <FormsManagement />,
      };
      return componentMap[documentId] || null; // Returns null instead of Error to allow dynamic rendering
    };

    // Map all routes from menuRoutes dynamically
    return menuRoutes.map(route => ({
      documentId: route.documentId,
      component: getComponentMap(route.documentId),
      path: route.path,
    }));
  }, [menuRoutes, t]);

  // Route structure rendering
  // RouteGuard manages all access logic (auth, validation, redirects)
  // AuthRoutes only defines the route structure
  return (
    <AppSuspense>
      <RouteGuard>
        <Routes>
          {/* Explicit handler for root path - MUST BE FIRST */}
          <Route path="/" element={<RootRedirect />} />

          {/* TEMPORARY: Visual Test Route */}
          <Route path="/visual-test-apploading" element={<AppLoadingVisualTest />} />

          {/* Public routes - do not require authentication */}
          {publicRoutes.map(route => (
            <Route key={route.documentId} path={route.path} element={route.component} />
          ))}

          {/* Protected routes - require authentication (managed by RouteGuard) */}
          <Route
            path="/"
            element={
              <LayoutSwitcher>
                <RequireMenuInitialized>
                  <RouteErrorBoundary>
                    <Outlet />
                  </RouteErrorBoundary>
                </RequireMenuInitialized>
              </LayoutSwitcher>
            }
          >
            {protectedRoutes.map(route => (
              <Route
                key={route.documentId}
                path={route.path}
                element={
                  route.component === null ? (
                    // Dynamic component - rendered by LayoutSwitcher
                    <LayoutSwitcher>
                      <RequireMenuInitialized>
                        <Outlet />
                      </RequireMenuInitialized>
                    </LayoutSwitcher>
                  ) : (
                    // Static component (fallback to Error if undefined)
                    route.component || <Error />
                  )
                }
              />
            ))}

            {/* Fixed system routes - not from menu */}
            <Route path="/settings/profile" element={<SettingsProfile />} />

            {/* Fallback for routes not found - RouteGuard manages the 404 */}
            <Route
              path="*"
              element={
                <div className="flex justify-center items-center h-screen w-full">
                  <Spin />
                </div>
              }
            />
          </Route>
        </Routes>
      </RouteGuard>
    </AppSuspense>
  );
};

export default AuthRoutes;
